import { cn, NoddiIcon } from 'noddi-ui-common';
import { useEffect } from 'react';
import { Button } from '../../../../../base/button/button';
import { NoddiBadge } from '../../../../Other/NoddiBadge';
import { resolveTableFilterTranslations, TableFilterTranslations } from '../../tableTranslationTypes';

const SIDEBAR_KEYBOARD_SHORTCUT = 'm';

interface FilterToggleButtonProps {
  activeFiltersCount: number;
  filterSidebarOpen?: boolean;
  setFilterSidebarOpen?: (open: boolean) => void;
  translations?: Partial<TableFilterTranslations>;
}

export function FilterToggleButton({
  activeFiltersCount,
  filterSidebarOpen,
  setFilterSidebarOpen,
  translations
}: FilterToggleButtonProps) {
  const texts = resolveTableFilterTranslations(translations);
  const hasActiveFilters = activeFiltersCount > 0;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setFilterSidebarOpen?.(!filterSidebarOpen);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filterSidebarOpen, setFilterSidebarOpen]);

  // The count sits inside the button, not pinned outside it: a badge overhanging the top-right
  // corner needed padding on the whole toolbar to survive an overflow-hidden dialog, and read as a
  // notification rather than as the state of the control it hangs off. `size-5` with no padding is
  // what keeps it a circle: a fixed height plus `min-w` lets the width track the glyph instead, and
  // 20px stays a whole number of device pixels at the browser zooms 18px falls between (80/90/125%),
  // where the two axes round to different pixel counts and the circle draws as an egg.
  return (
    <Button
      variant='outline'
      aria-pressed={hasActiveFilters}
      className={cn(
        hasActiveFilters &&
          'border-darkPurple bg-lightPurple text-darkPurple hover:bg-lightPurple hover:text-darkPurple'
      )}
      onClick={() => setFilterSidebarOpen?.(!filterSidebarOpen)}
    >
      <NoddiIcon name='Filter' />
      {texts.filters}
      {hasActiveFilters && (
        <NoddiBadge className='size-5 border-darkPurple bg-darkPurple p-0 text-[11px] leading-none font-semibold text-white tabular-nums'>
          {activeFiltersCount}
        </NoddiBadge>
      )}
    </Button>
  );
}
